import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton
} from '@mui/material';
import {
  LocationOn,
  MyLocation,
  Search,
  CheckCircle,
  Close
} from '@mui/icons-material';

const LocationPopup = ({ open, onClose, onLocationSelect }) => {
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (open) {
      getCurrentLocation();
    }
  }, [open]);

  const getCurrentLocation = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by this browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          
          // Get address from coordinates using reverse geocoding
          try {
            // Get auth token
            const customerToken = localStorage.getItem('customerToken');
            const token = localStorage.getItem('token');
            const authToken = customerToken || token;
            
            const headers = { 'Content-Type': 'application/json' };
            if (authToken) {
              headers['Authorization'] = `Bearer ${authToken}`;
            }
            
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify({ lat: latitude, lng: longitude })
            });
            const data = await response.json();
            
            if (data.success && data.data) {
              const fullAddress = data.data.formatted_address;
              setAddress(fullAddress);
              setSelectedLocation({
                address: fullAddress,
                latitude,
                longitude
              });
            }
          } catch (error) {
            console.error('Reverse geocoding failed:', error);
            setAddress('Location detected');
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError('Failed to get current location. Please enter your address manually.');
        }
      );
    } catch (error) {
      setError('Failed to get current location');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      // This would typically call a geocoding service
      // For now, we'll simulate with mock data
      const mockSuggestions = [
        { address: `${searchQuery}, New York, NY`, latitude: 40.7128, longitude: -74.0060 },
        { address: `${searchQuery}, Los Angeles, CA`, latitude: 34.0522, longitude: -118.2437 },
        { address: `${searchQuery}, Chicago, IL`, latitude: 41.8781, longitude: -87.6298 },
      ];
      
      setSuggestions(mockSuggestions);
    } catch (error) {
      setError('Failed to search location');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLocation = (suggestion) => {
    setSelectedLocation(suggestion);
    setSearchQuery(suggestion.address);
    setSuggestions([]);
  };

  const handleConfirmLocation = async () => {
    if (selectedLocation) {
      // Save location to localStorage or context
      localStorage.setItem('userLocation', JSON.stringify(selectedLocation));
      onLocationSelect(selectedLocation);
      onClose();
    }
  };

  const handleSkipLocation = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
          color: 'white',
          boxShadow: '0 20px 60px rgba(30, 58, 138, 0.3)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            Select Your Location
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ 
        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        color: 'white',
        pt: 3
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}>
            {error}
          </Alert>
        )}
        
        {/* Current Location Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'white' }}>
            Current Location
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <MyLocation />}
            onClick={getCurrentLocation}
            disabled={loading}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'white',
              py: 1.5,
              borderRadius: '12px',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            {loading ? 'Getting Location...' : 'Use Current Location'}
          </Button>
          {address && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }}>
                📍 {address}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2 }} />
        
        {/* Search Location Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'white' }}>
            Search Location
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter your address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchLocation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white'
                },
                '& input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255,255,255,0.7)',
                    opacity: 1
                  }
                }
              }
            }}
          />
          
          {suggestions.length > 0 && (
            <List sx={{ 
              mt: 2, 
              backgroundColor: 'rgba(255,255,255,0.05)', 
              borderRadius: '12px',
              maxHeight: 200,
              overflow: 'auto'
            }}>
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleSelectLocation(suggestion)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <LocationOn sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={suggestion.address}
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        color: 'white',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
        color: 'white',
        p: 3,
        pt: 1
      }}>
        <Button
          onClick={handleSkipLocation}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Skip for now
        </Button>
        <Button
          onClick={handleConfirmLocation}
          disabled={!selectedLocation}
          variant="contained"
          sx={{
            backgroundColor: 'white',
            color: '#1E3A8A',
            textTransform: 'none',
            borderRadius: '12px',
            px: 3,
            py: 1,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.9)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.5)'
            }
          }}
        >
          Confirm Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationPopup; 